import React from "react";
import { Tdah3Scores, TDAH3_SEVERITY_LEVELS } from "../tdah3Constants";

interface Tdah3ScoreDisplayProps {
  scores: Tdah3Scores;
  interpretations?: Record<string, any>;
}

const ScoreCard = ({ score }: { score: Tdah3Scores[keyof Tdah3Scores] }) => {
  const getSeverityColor = (interpretation: string) => {
    switch (interpretation) {
      case "Funcionamiento dentro de lo esperado":
        return "text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400";
      case "Dificultades moderadas":
        return "text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "Dificultades significativas":
        return "text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "text-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const getProgressColor = (interpretation: string) => {
    switch (interpretation) {
      case "Funcionamiento dentro de lo esperado":
        return "bg-green-500 dark:bg-green-400";
      case "Dificultades moderadas":
        return "bg-yellow-500 dark:bg-yellow-400";
      case "Dificultades significativas":
        return "bg-red-500 dark:bg-red-400";
      default:
        return "bg-gray-500 dark:bg-gray-400";
    }
  };

  const percentage = (score.score / score.max_score) * 100;

  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-navy-700/50 dark:bg-navy-800/50 dark:shadow-navy-700/20">
      <div className="flex flex-col space-y-4">
        {/* Title and Interpretation */}
        <div>
          <h4 className="text-xl font-semibold text-navy-700 dark:text-white">
            {score.name}
          </h4>
          <span
            className={`mt-2 inline-block rounded-lg px-3 py-1 text-sm font-medium ${getSeverityColor(
              score.interpretation
            )}`}
          >
            {score.interpretation}
          </span>
        </div>

        {/* Score and Progress */}
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <div className="flex items-baseline space-x-1">
              <span className="text-3xl font-bold text-navy-700 dark:text-white">
                {score.score}
              </span>
              <span className="text-lg text-gray-500 dark:text-gray-400">
                / {score.max_score}
              </span>
            </div>
            <span className="text-lg font-semibold text-navy-700 dark:text-white">
              {percentage.toFixed(0)}%
            </span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
            <div
              className={`h-full rounded-full transition-all ${getProgressColor(
                score.interpretation
              )}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        {/* Questions Answered */}
        <div className="flex items-center gap-x-2 pt-2">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {score.questions_answered} preguntas respondidas
          </p>
        </div>
      </div>
    </div>
  );
};

const Tdah3ScoreDisplay: React.FC<Tdah3ScoreDisplayProps> = ({ scores }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <ScoreCard score={scores.control_inhibitorio} />
        <ScoreCard score={scores.memoria_trabajo} />
        <ScoreCard score={scores.planificacion} />
        <ScoreCard score={scores.flexibilidad} />
        <ScoreCard score={scores.regulacion_emocional} />
        <ScoreCard score={scores.iniciativa} />
      </div>

      {/* Legend */}
      <div className="mt-6 rounded-xl border border-gray-100 bg-white p-6 shadow-sm dark:border-navy-700/50 dark:bg-navy-800/50">
        <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
          Interpretación de Puntuaciones
        </h4>
        <div className="flex flex-col space-y-2">
          {Object.values(TDAH3_SEVERITY_LEVELS).map((level) => (
            <div
              key={level.text}
              className={`flex items-center rounded-lg px-4 py-3 text-sm font-medium ${
                level.text === "Funcionamiento dentro de lo esperado"
                  ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                  : level.text === "Dificultades moderadas"
                  ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
                  : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              {level.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Tdah3ScoreDisplay;
